import { useState, useEffect } from 'react';
import { Building2, Edit2, Globe, MapPin, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Company, AdminRole } from '@/types';
import { supabase } from '@/lib/supabase';
import { hasPermission } from '@/lib/permissions';
import { toast } from 'sonner';

export function CompanyPage() {
  const [company, setCompany] = useState<Company | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isLogoDialogOpen, setIsLogoDialogOpen] = useState(false);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  useEffect(() => {
    fetchCompany();
  }, []);

  const fetchCompany = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // First get the user's profile to get the company_id
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('company_id')
        .eq('id', user.id)
        .single();

      if (profileError) throw profileError;

      if (profile?.company_id) {
        const { data, error } = await supabase
          .from('companies')
          .select('*')
          .eq('id', profile.company_id)
          .single();

        if (error) throw error;
        setCompany(data);
      }
    } catch (error) {
      console.error('Error fetching company:', error);
      toast.error('Failed to load company information');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!company) return;

    try {
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError) {
        console.error('Authentication error:', authError);
        toast.error('Authentication failed');
        return;
      }
      if (!user) {
        toast.error('User not authenticated');
        return;
      }

      // Validate input
      if (!company.name || !company.description || !company.location) {
        toast.error('Please fill in all required fields');
        return;
      }

      // Verify user's association with the company
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('company_id, admin_role')
        .eq('id', user.id)
        .single();

      if (profileError) {
        console.error('Profile fetch error:', profileError);
        toast.error('Failed to verify user profile');
        return;
      }

      // Check if user has permission to update the company
      const canUpdateCompany =
        profile.company_id === company.id ||
        (await hasPermission(profile.admin_role as AdminRole, 'manageUsers'));

      if (!canUpdateCompany) {
        console.error('Unauthorized company update attempt', {
          user_company_id: profile.company_id,
          attempted_company_id: company.id,
          admin_role: profile.admin_role
        });
        toast.error('You do not have permission to update this company');
        return;
      }

      let companyId = company.id;
      
      // Prepare company data
      const companyData = {
        name: company.name,
        description: company.description,
        location: company.location,
        website: company.website || null,
        logo_url: company.logo_url || null
      };

      // If no company exists, create one
      if (!companyId) {
        const { data: newCompany, error: createError } = await supabase
          .from('companies')
          .insert([companyData])
          .select()
          .single();

        if (createError) {
          console.error('Create company error:', createError);
          toast.error(`Failed to create company: ${createError.message}`);
          return;
        }
        companyId = newCompany.id;

        // Update user's profile with the new company_id
        const { error: profileUpdateError } = await supabase
          .from('profiles')
          .update({ company_id: companyId })
          .eq('id', user.id);

        if (profileUpdateError) {
          console.error('Profile update error:', profileUpdateError);
          toast.error(`Failed to update profile: ${profileUpdateError.message}`);
          return;
        }
      } else {
        // Update existing company
        const { error: updateError } = await supabase
          .from('companies')
          .update(companyData)
          .eq('id', companyId);

        if (updateError) {
          console.error('Update company error:', updateError);
          
          // Detailed error logging for RLS violation
          if (updateError.code === '42501') {
            console.error('Row-level security violation details:', {
              user_id: user.id,
              company_id: companyId,
              profile_company_id: profile.company_id,
              error_message: updateError.message
            });
            toast.error('You do not have permission to update this company');
          } else {
            toast.error(`Failed to update company: ${updateError.message}`);
          }
          return;
        }
      }
      
      setIsEditing(false);
      toast.success('Company information updated successfully');
      fetchCompany(); // Refresh company data
    } catch (error) {
      console.error('Unexpected error updating company:', error);
      toast.error('An unexpected error occurred');
    }
  };

  const handleLogoUpload = async () => {
    if (!logoFile || !company) return;

    try {
      setUploadingLogo(true);
      const fileExt = logoFile.name.split('.').pop();
      const fileName = `${Date.now()}.${fileExt}`;
      const filePath = `company-logos/${fileName}`;

      // Upload file to Supabase Storage
      const { error: uploadError } = await supabase.storage
        .from('public')
        .upload(filePath, logoFile);

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('public')
        .getPublicUrl(filePath);

      // Update company with new logo URL
      const { error: updateError } = await supabase
        .from('companies')
        .update({ logo_url: publicUrl })
        .eq('id', company.id);

      if (updateError) throw updateError;

      toast.success('Logo updated successfully');
      setIsLogoDialogOpen(false);
      fetchCompany(); // Refresh company data
    } catch (error) {
      console.error('Error uploading logo:', error);
      toast.error('Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-96">Loading...</div>;
  }

  const emptyCompany: Company = {
    id: '',
    name: '',
    description: '',
    location: '',
    website: '',
    logo_url: '',
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  };

  const displayCompany = company || emptyCompany;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">Company Profile</h1>
        <div className="space-x-2">
          <Button onClick={() => setIsLogoDialogOpen(true)} variant="outline">
            <Upload className="mr-2 h-4 w-4" />
            Update Logo
          </Button>
          <Button onClick={() => setIsEditing(!isEditing)}>
            <Edit2 className="mr-2 h-4 w-4" />
            {isEditing ? 'Cancel' : 'Edit Profile'}
          </Button>
        </div>
      </div>

      <Dialog open={isLogoDialogOpen} onOpenChange={setIsLogoDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Update Company Logo</DialogTitle>
            <DialogDescription>
              Upload a new logo for your company. Recommended size: 200x200px.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Input
              type="file"
              accept="image/*"
              onChange={(e) => setLogoFile(e.target.files?.[0] || null)}
            />
            <div className="flex justify-end space-x-2">
              <Button variant="outline" onClick={() => setIsLogoDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleLogoUpload}
                disabled={!logoFile || uploadingLogo}
              >
                {uploadingLogo ? 'Uploading...' : 'Upload Logo'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Card>
        <CardHeader>
          <div className="flex items-center space-x-4">
            {displayCompany.logo_url ? (
              <img
                src={displayCompany.logo_url}
                alt={displayCompany.name}
                className="h-16 w-16 rounded-lg object-cover"
              />
            ) : (
              <div className="h-16 w-16 rounded-lg bg-muted flex items-center justify-center">
                <Building2 className="h-8 w-8 text-muted-foreground" />
              </div>
            )}
            <div>
              <h2 className="text-2xl font-semibold">{displayCompany.name || 'Your Company Name'}</h2>
              <div className="flex items-center space-x-4 text-muted-foreground">
                <div className="flex items-center">
                  <MapPin className="h-4 w-4 mr-1" />
                  {displayCompany.location || 'Location'}
                </div>
                {displayCompany.website && (
                  <div className="flex items-center">
                    <Globe className="h-4 w-4 mr-1" />
                    <a href={displayCompany.website} target="_blank" rel="noopener noreferrer" className="hover:underline">
                      Website
                    </a>
                  </div>
                )}
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isEditing ? (
            <form onSubmit={handleSave} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Company Name</label>
                <Input
                  value={displayCompany.name}
                  onChange={(e) => setCompany({ ...displayCompany, name: e.target.value })}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Description</label>
                <Textarea
                  value={displayCompany.description}
                  onChange={(e) => setCompany({ ...displayCompany, description: e.target.value })}
                  required
                  rows={4}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Location</label>
                <Input
                  value={displayCompany.location}
                  onChange={(e) => setCompany({ ...displayCompany, location: e.target.value })}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Website</label>
                <Input
                  type="url"
                  value={displayCompany.website || ''}
                  onChange={(e) => setCompany({ ...displayCompany, website: e.target.value })}
                  placeholder="https://example.com"
                />
              </div>
              <div className="flex justify-end space-x-4">
                <Button type="button" variant="outline" onClick={() => setIsEditing(false)}>
                  Cancel
                </Button>
                <Button type="submit">Save Changes</Button>
              </div>
            </form>
          ) : (
            <div className="prose max-w-none">
              <p className="text-muted-foreground">{displayCompany.description || 'Add a description of your company'}</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}